/* eslint-disable no-param-reassign */

import { network } from 'boot/network';
import { liveQuery } from 'dexie';
import {
  arrayRemove,
  arrayUnion,
  documentId,
  FieldValue,
  onSnapshot,
  QuerySnapshot,
  Timestamp,
} from 'firebase/firestore';
import { chain, chunk, cloneDeep, forOwn, isEmpty, isFunction, mapValues, orderBy } from 'lodash';
import { ENCRYPTED_FIELDS } from 'src/constants/encryption';
import { firestoreUnbindings as useFirestoreUnbindingsStore } from 'src/store/firestoreUnbindings';
import AppUtils from 'src/utils/AppUtils';
import { BACKUP_NOTES } from 'src/utils/backupUtils';
import { decryptDataObject, readSecret } from 'src/utils/CryptoUtils';
import { logEvent } from 'src/utils/LogEvent';

/** @typedef {import('firebase/firestore').Unsubscribe} Unsubscribe */
/** @typedef {import('firebase/firestore').DocumentData} DocumentData */

/** @typedef {{seconds: number, nanoseconds: number} | {_seconds: number, _nanoseconds: number}} TimestampObject */

/**
 * @typedef {Object} QueryFiled
 * @property {FieldPath | string} [field=documentId()]
 * @property {'in' | 'not-in' | 'array-contains' | 'array-contains-any'} [operator='in']
 * @property {string[]} [clauses=[]]
 */

/**
 * @typedef {Object} PiniaStoreBinding
 * @property {Object} store - Pinia store instance
 * @property {string} property - Property name in the store to bind data to
 * @property {string} [bindingId] - Unique identifier for this binding (auto-generated if not provided)
 */

const INTERNAL_ID_PREFIX = 'idxDb';

/**
 * Generate a binding key for Firestore unbindings using the store's ID and state field.
 *
 * @param {Object} store - Pinia store instance
 * @param {string} stateField - The state field to bind/unbind (e.g., 'associatedClients')
 * @returns {string} Binding key in the format `storeId/stateField` (e.g., 'auth/associatedClients')
 * @throws {Error} If store.$id or stateField is invalid
 */
export function generateBindingKey(store, stateField) {
  if (!store || typeof store.$id !== 'string' || !store.$id) {
    throw new Error('Invalid store: store.$id must be a non-empty string');
  }
  if (typeof stateField !== 'string' || !stateField) {
    throw new Error('Invalid stateField: must be a non-empty string');
  }
  return `${store.$id}/${stateField}`;
}

/**
 * Get the data from the FieldValue instance.
 * @param {FieldValue | Object} fieldValue
 * @return {any}
 */
export function getFieldValueData(fieldValue) {
  if (!(fieldValue instanceof FieldValue)) {
    return null;
  }

  return chain(fieldValue)
    .omit('_methodName')
    .values()
    .flatten()
    .value();
}

/**
 * Get a new FieldValue instance with the original method but with a new data.
 *
 * @param {FieldValue} originalFieldValue
 * @param {Array<any>} newData
 *
 * @return {FieldValue} FieldValue with new data
 */
export function getFieldValueWithUpdatedData(originalFieldValue, newData) {
  if (!(originalFieldValue instanceof FieldValue)) {
    return originalFieldValue;
  }

  // eslint-disable-next-line no-underscore-dangle
  const fieldValueMethod = originalFieldValue._methodName;
  if (!fieldValueMethod) {
    return originalFieldValue;
  }

  switch (fieldValueMethod) {
    case 'arrayUnion':
      return arrayUnion(...newData);
    case 'arrayRemove':
      return arrayRemove(...newData);
    default:
      return originalFieldValue;
  }
}

async function decryptData(data, path, parentSchemaKey = null) {
  if (!network.offlineMode && path !== BACKUP_NOTES) {
    return data;
  }

  const decryptionKey = path === BACKUP_NOTES
    ? await readSecret('backupDecryptionKey')
    : await readSecret('decryptionKey');

  if (!decryptionKey) {
    return data;
  }

  const actions = Object.keys(data).map(async (key) => {
    const schemaKey = parentSchemaKey ? `${parentSchemaKey}.${key}` : key;
    const value = data[key];

    if (ENCRYPTED_FIELDS[path]?.includes(schemaKey)) {
      if (Array.isArray(value) && !isEmpty(value)) {
        data[key] = await Promise.all(value.map(async (item) => {
          if (typeof item === 'object' && !isEmpty(value) && !value.encrypted) {
            return decryptData(item, path, schemaKey);
          }
          return decryptDataObject(item, decryptionKey.value);
        }));
      } else if (typeof value === 'object' && !isEmpty(value) && !value.encrypted) {
        data[key] = await decryptData(value, path, schemaKey);
      }

      try {
        data[key] = await decryptDataObject(value, decryptionKey.value);
      } catch (err) {
        console.error('Decryption error', value, err);
      }
    }
  });

  await AppUtils.allSettled(actions);
  return data;
}

export async function decrypt(data, path) {
  if (!data) {
    return data;
  }

  if (Array.isArray(data)) {
    return Promise.all(data.map((doc) => decryptData(cloneDeep(doc), path)));
  }

  return decryptData(cloneDeep(data), path);
}

/**
 * @param {DocumentSnapshot<DocumentData> | QuerySnapshot<DocumentData>} snapshot
 * @return {Object & {id: string} | Array<Object & {id: string}> | null}
 */
function serializeData(snapshot) {
  if (snapshot instanceof QuerySnapshot) {
    return snapshot.docs.map((docSnapshot) => {
      return {
        id: docSnapshot.id,
        ...docSnapshot.data(),
      };
    });
  }

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
}

/**
 * @param {FirestoreError} err
 * @param {string} bindingKey
 * @param {DocumentReference<DocumentData> | Query<DocumentData>} ref
 */
function handleDbError(err, bindingKey, ref) {
  // eslint-disable-next-line no-underscore-dangle
  const path = ref.type === 'document'
    ? ref.path
    // eslint-disable-next-line no-underscore-dangle
    : ref._query?.path?.segments.join('/');

  logEvent(err, {
    context: {
      context_data: {
        bindingKey,
        path,
        ...(ref.type === 'document' ? { id: ref.id } : {}),
      },
    },
  });
  console.error(`Error binding a ${path} ${ref.type} with ${bindingKey}: ${err.message}`);
}

/**
 * @param {Error} err
 * @param {string} bindingKey - The binding key in the format `storeName/propertyName` (e.g., 'auth/associatedClients')
 * @param {IdxDbRef} ref
 */
function handleIdxDbError(err, bindingKey, ref) {
  logEvent(err, {
    context: {
      context_data: {
        bindingKey,
        path: ref.path,
        ...(ref.type === 'document' ? { id: ref.id } : {}),
      },
    },
  });
  console.error(`Error binding a ${ref.path} ${ref.type} with ${bindingKey}: ${err.message}`);
}

/**
 * @param {TimestampObject} timeObject
 * @return {Timestamp}
 */
export function firebaseTimestampObjectToTimestamp(timeObject) {
  // eslint-disable-next-line no-underscore-dangle
  const seconds = timeObject.seconds || timeObject._seconds || 0;
  // eslint-disable-next-line no-underscore-dangle
  const nanos = timeObject.nanoseconds || timeObject._nanoseconds || 0;
  const millis = (seconds * 1000) + (nanos / 1000000);
  return Timestamp.fromMillis(millis);
}

/**
 * @param {any} object
 * @return {object is TimestampObject}
 */
export function isTimestampObject(object) {
  return object
    && typeof object === 'object'
    && !(object instanceof Timestamp)
    && (
      ('seconds' in object && 'nanoseconds' in object) || ('_seconds' in object && '_nanoseconds' in object)
    );
}

/**
 * Convert all firebase Timestamp objects within an object to Firestore Timestamps.
 * @param {Object} obj
 * @returns {Object}
 */
export function convertfirebaseTimestampObjectsToTimestamps(obj) {
  return mapValues(obj, (value) => (
    isTimestampObject(value) ? firebaseTimestampObjectToTimestamp(value) : value
  ));
}

/**
 * Reverse GenericIndexedDb.#convertToIndexedDbDocumentId for a single-level
 * subcollection path: `{root}/{parentDocId}/{subcollection}`.
 *
 * @param {string} indexedDbDocId
 * @param {string} collectionPath
 * @return {string}
 */
function deserializeIndexedDbDocumentId(indexedDbDocId, collectionPath) {
  const idxPrefix = `${INTERNAL_ID_PREFIX}_`;

  if (!indexedDbDocId?.startsWith(idxPrefix)) {
    return indexedDbDocId;
  }

  const parts = collectionPath.split('/').filter(Boolean);
  if (parts.length !== 3) {
    return indexedDbDocId.slice(idxPrefix.length);
  }

  const [, parentDocId] = parts;
  const fullPrefix = `${idxPrefix}${parentDocId}_`;
  if (indexedDbDocId.startsWith(fullPrefix)) {
    return indexedDbDocId.slice(fullPrefix.length);
  }

  return indexedDbDocId.slice(idxPrefix.length);
}

/**
 * Check whether a Firestore collection path matches a configured subcollection
 * that uses the IndexedDB `idxDb_{parentDocId}_...` id prefix convention.
 *
 * @param {string} collectionPath
 * @param {Array<{collection: string, subcollections: string[]}>} config
 * @return {boolean}
 */
function isSubCollectionsWithPrefix(collectionPath, config) {
  const parts = collectionPath.split('/').filter(Boolean);
  if (parts.length < 3) {
    return false;
  }

  const rootCollection = parts[0];
  const subcollectionName = parts[parts.length - 1];

  return config.some(({ collection, subcollections }) => (
    rootCollection === collection
      && subcollections.includes(subcollectionName)
  ));
}

/**
 * @param {Object & { id: string }} doc
 * @param {string} collectionName
 * @return {Object & { id: string }}
 */
function serializeIdxDbDoc(doc, collectionName) {
  const resultDoc = { ...doc };
  if (resultDoc.id) {
    // collections that can have id like `N153Ly5nDG93UO5PR56s_118943`
    const collectionsWithUnderscoreIds = ['appointments'];

    // subcollections that can have id like `idxDb_ippOrVQRgBrDm7RBBsyh_hr_2icCk6RWP5h10SGIo3qv`
    const subcollectionsWithPrefix = [{
      collection: 'clinicSettings',
      subcollections: ['dataFields'],
    }];

    if (isSubCollectionsWithPrefix(collectionName, subcollectionsWithPrefix)) {
      resultDoc.id = deserializeIndexedDbDocumentId(doc.id, collectionName);
    } else {
      // use some() to support subcollection when collection name looks like `{collection}/{parentId}/{subCollection}
      const onlyCutPrefix = collectionsWithUnderscoreIds.some((collection) => collectionName.includes(collection));
      resultDoc.id = onlyCutPrefix
        ? doc.id.slice(`${INTERNAL_ID_PREFIX}_`.length)
        : doc.id.split('_').pop() || doc.id;
    }
  }

  forOwn(resultDoc, (value, key) => {
    if (Array.isArray(value)) {
      // eslint-disable-next-line no-use-before-define
      resultDoc[key] = serializeIdxDbData(value, collectionName);
    }

    const isTimestamp = isTimestampObject(value);

    if (isTimestamp) {
      resultDoc[key] = firebaseTimestampObjectToTimestamp(value);
    }

    const isDate = value instanceof Date;
    const isPlainObject = AppUtils.isPlainObject(value);

    if (isPlainObject && !isTimestamp && !isDate) {
      resultDoc[key] = serializeIdxDbDoc(value, collectionName);
    }
  });

  return resultDoc;
}

/**
 * @param {Object | Object[]} data
 * @param {string} collectionName
 * @return {Object & { id: string } | Array<Object & {id: string}> | null}
 */
export function serializeIdxDbData(data, collectionName) {
  if (!data) {
    return null;
  }

  if (Array.isArray(data)) {
    return data.map((doc) => ((typeof doc === 'object' && !(doc instanceof ArrayBuffer))
      ? serializeIdxDbDoc(doc, collectionName)
      : doc));
  }

  if (typeof data === 'object' && !(data instanceof ArrayBuffer)) {
    return serializeIdxDbDoc(data, collectionName);
  }

  return data;
}

/**
 * Manage Firestore unbinding functions in the Pinia Firestore store.
 *
 * @param {Unsubscribe | null} unbindFunc - The unsubscription function to store or null to remove
 * @param {string} bindingKey - The binding key in the format `storeName/propertyName` (e.g., 'auth/associatedClients')
 */
function setFirestoreUnbinding(unbindFunc, bindingKey) {
  const firestoreUnbindingsStore = useFirestoreUnbindingsStore();
  firestoreUnbindingsStore.setFirestoreUnbinding(unbindFunc, bindingKey);
}

/**
 * Bind real-time updating data to the store using provided mutation.
 *
 * @param {PiniaStoreBinding} binding - Pinia store binding configuration
 * @param {IdxDbRef} ref - indexed DB resolved query with additional info
 * @param {*} [defaultValue] - value to be set if the document isn't exists in DB
 * @return {Promise<Object & {id: string} | Array<Object & {id: string}> | null>}
 */
export async function bindIndexedDbStore(binding, ref, defaultValue = null) {
  return new Promise((resolve, reject) => {
    const { store, property, action } = binding;
    const bindingKey = generateBindingKey(store, property);
    const observable = liveQuery(ref.query);

    const subscription = observable.subscribe({
      next: async (data) => {
        let resultData = await decrypt(data, ref.path);

        resultData = serializeIdxDbData(resultData, ref.path) || defaultValue;

        if (ref.orderBy) {
          resultData = orderBy(resultData, ref.orderBy, ref.orderDirection);
        }
        // We use 'property' when there is a direct change to store property.
        // We use 'action' if a change to store property has side effects.
        if (action && isFunction(store[action])) {
          store[action](resultData);
        } else {
          store[property] = resultData;
        }

        resolve(resultData);
      },
      error: (err) => {
        handleIdxDbError(err, bindingKey, ref);
        reject(err);
      },
    });

    const unbindFunc = subscription.unsubscribe;
    setFirestoreUnbinding(unbindFunc, bindingKey);
  });
}

/**
 * Bind real-time updating data to the store using provided mutation.
 *
 * @param {PiniaStoreBinding} binding - Pinia store binding configuration
 * @param {DocumentReference<DocumentData> | Query<DocumentData> | IdxDbRef} ref - firestore document reference.
 * @param {*} [defaultValue] - value to be set if the document isn't exists in DB
 * @return {Promise<Object & {id: string} | Array<Object & {id: string}> | null>}
 */
export async function bindDataBaseStore(
  binding,
  ref,
  defaultValue = null,
) {
  if (network.offlineMode && ref.query) {
    return bindIndexedDbStore(binding, ref, defaultValue);
  }

  return new Promise((resolve, reject) => {
    const { store, property, action } = binding;
    const bindingKey = generateBindingKey(store, property);

    const unbindFunc = onSnapshot(ref, {
      next: (snapshot) => {
        const data = serializeData(snapshot) || defaultValue;

        // We use 'property' when there is a direct change to store property.
        // We use 'action' if a change to store property has side effects.
        if (action && isFunction(store[action])) {
          store[action](data);
        } else {
          store[property] = data;
        }

        resolve(data);
      },
      error: (err) => {
        handleDbError(err, bindingKey, ref);
        reject(err);
      },
    });

    setFirestoreUnbinding(unbindFunc, bindingKey);
  });
}

/**
 * Bind real-time updating data in chunks to the store using provided mutation.
 *
 * @param {PiniaStoreBinding} binding - Pinia store binding configuration
 * @param {GenericDB} db - database instance, should be extended from the GenericDB.
 * @param {QueryFiled} [queryFiled={}] - query by array field options.
 * @param {Array[]} [additionalQueries=[]] - additional queries to be added to the main one.
 * @param {number} [chunksSize=30] - size of the chunks to split the clauses.
 * @param {*} [defaultValue=[]] - value to be set if documents chunk doesn't exist in DB
 * @return {Promise<Array<Object & {id: string}>>}
 */
export async function bindDataBaseStoreByArrayOperator(
  binding,
  db,
  { field = documentId(), operator = 'in', clauses = [] } = {},
  additionalQueries = [],
  chunksSize = 30,
  defaultValue = [],
) {
  // We don't need to split the query into chunks for IndexDB,
  // because it doesn't have the query complexity limitations that Firebase does.
  if (network.offlineMode) {
    const ref = db.getQuery([
      [field, operator, clauses],
      ...additionalQueries,
    ]);

    return bindIndexedDbStore(binding, ref, defaultValue);
  }

  const chunks = chunk(clauses, chunksSize);
  const unbindFunctions = [];
  const chunkDataMap = new Map();

  const { store, property, action } = binding;
  const bindingKey = generateBindingKey(store, property);

  const resolveCompletions = (resolve, reject, chunkIndex, data) => {
    chunkDataMap.set(chunkIndex, data);

    const aggregatedData = [...chunkDataMap.values()].flat();

    // We use 'property' when there is a direct change to store property.
    // We use 'action' if a change to store property has side effects.
    if (action && isFunction(store[action])) {
      store[action](aggregatedData);
    } else {
      store[property] = aggregatedData;
    }

    resolve(aggregatedData);
  };

  const aggregatedPromise = Promise.all(chunks.map((chunkedClauses, chunkIndex) => {
    const ref = db.getQuery([
      [field, operator, chunkedClauses],
      ...additionalQueries,
    ]);

    return new Promise((resolve, reject) => {
      const unbindFunc = onSnapshot(ref, {
        next: (snapshot) => {
          const data = serializeData(snapshot) || defaultValue;
          resolveCompletions(resolve, reject, chunkIndex, data);
        },
        error: (err) => {
          handleDbError(err, bindingKey, ref);
          reject(err);
        },
      });

      unbindFunctions.push(unbindFunc);
    });
  }))
    .then((results) => results.flat());

  const combinedUnbindFunc = () => {
    unbindFunctions.forEach((unbindFunc) => unbindFunc());
  };

  setFirestoreUnbinding(combinedUnbindFunc, bindingKey);

  return aggregatedPromise;
}

/**
 * Unbind real-time updating data from the Pinia store.
 *
 * @param {PiniaStoreBinding} binding - Pinia store binding configuration
 * @param {any} [resetValue=null] - The value to set the state field to after unbinding
 * @param {boolean} [shouldReset] - should reset state field after the unbinding. // todo: rewrite without flag argument
 */
export function unbindDataBaseStore(binding, resetValue = null, shouldReset = true) {
  const { store, property, action } = binding;
  const bindingKey = generateBindingKey(store, property);
  setFirestoreUnbinding(null, bindingKey);

  if (shouldReset) {
    if (action && typeof store[action] === 'function') {
      store[action](resetValue);
    } else {
      store[property] = resetValue;
    }
  }
}
